import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import Modal from 'tio-ui/components/modal';
import Button from 'tio-ui/components/button';
import jsonPrettyPrint from '../../../../../helpers/json-pretty-print.ts';
import { flattenPolicy } from '../../../../../utils/flatten-policy.ts';

/**
 * The policy an AI recommendation was judged under, opened from the `Policy vN`
 * caption on the analysis card.
 *
 * The content is read straight out of the recommendation context rather than
 * fetched — `reimbursement_policy` is the exact composite the model was given,
 * and the approval route already ships the whole context blob. That also makes
 * it the honest source: re-fetching the partner's current policy would show a
 * revision this recommendation was never judged against.
 */
export interface PolicyDetailModalSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    /** The composite policy exactly as stored in the recommendation context. */
    policy: unknown;
    /**
     * The rendered caption, e.g. "Policy v35". Absent when a row stored a
     * policy but no version to stamp it with.
     */
    versionLabel?: string;
    /** The raw stored provenance string, e.g. "634-partner_policy-v35". */
    rawVersion?: string;
  };
}

// A finite map of literal classes — dynamic class names cannot be proven to
// resolve against the frozen utility set (scripts/check-resolvable-classes.mjs).
// Deeper rows clamp to the last entry rather than marching off the panel.
const INDENT_CLASSES = ['pl-0', 'pl-4', 'pl-8', 'pl-12', 'pl-16'];

class PolicyDetailModal extends Component<PolicyDetailModalSignature> {
  @tracked showRaw = false;

  get rows() {
    return flattenPolicy(this.args.policy).map((row) => ({
      ...row,
      indentClass: INDENT_CLASSES[Math.min(row.depth, INDENT_CLASSES.length - 1)],
    }));
  }

  toggleRaw = () => {
    this.showRaw = !this.showRaw;
  };

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @size="2xl"
      @ariaLabel="Policy detail"
      data-test-policy-detail-modal
    >
      <:header>
        <h3 class="tio-text-heading tio-fg-primary">{{if @versionLabel @versionLabel "Policy"}}</h3>
        {{#if @rawVersion}}
          <p class="tio-text-caption" data-test-policy-raw-version>{{@rawVersion}}</p>
        {{/if}}
      </:header>
      <:body>
        <div class="tio-row justify-between items-start gap-3 mb-3">
          <p class="tio-text-caption">
            The policy this recommendation was judged against, as it was given to the model.
          </p>
          <Button
            @appearance="minimal"
            @size="sm"
            @testHook="ai-analysis-policy__raw-toggle"
            {{on "click" this.toggleRaw}}
            data-test-policy-raw-toggle
          >
            {{if this.showRaw "View formatted" "View raw JSON"}}
          </Button>
        </div>

        {{#if this.showRaw}}
          <div data-test-policy-raw>{{htmlSafe (jsonPrettyPrint @policy)}}</div>
        {{else}}
          <div data-test-policy-rows>
            {{#each this.rows as |row|}}
              <div class="{{row.indentClass}} py-1">
                {{#if row.isHeading}}
                  <p class="tio-text-base-semibold tio-fg-primary">{{row.label}}</p>
                {{else}}
                  <p class="tio-text-caption">{{row.label}}</p>
                  {{#if row.items}}
                    <ul class="list-disc pl-5">
                      {{#each row.items as |item|}}
                        <li class="text-sm tio-fg-primary">{{item}}</li>
                      {{/each}}
                    </ul>
                  {{else}}
                    <p class="text-sm whitespace-pre-wrap tio-fg-primary">{{row.value}}</p>
                  {{/if}}
                {{/if}}
              </div>
            {{/each}}
          </div>
        {{/if}}
      </:body>
    </Modal>
  </template>
}

export default PolicyDetailModal;
